import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * useState backed by localStorage. Survives reloads.
 *
 * Generic enough for booleans, strings, JSON-serialisable objects.
 *
 * `T` is the caller's claim about what this key holds, and the `as T` below is
 * the one place it goes unchecked: localStorage is a string store shared with
 * every past version of this app, so a key whose shape changed hands back the
 * OLD shape wearing the new type. The try/catch covers unparseable JSON only,
 * never wrong-but-valid JSON. A caller whose shape has changed should validate
 * what it gets rather than trust the parameter.
 */
export function usePersistedState<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return initialValue;
      return JSON.parse(raw) as T;
    } catch {
      return initialValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
}
